import { TIMING } from '../constants';

// Only makes each URL unique so no cache answers for the network; it needs no randomness.
let sequence = 0;
const nonce = () => {
    sequence += 1;
    return `${Date.now()}-${sequence}`;
};

// A bare GET with no custom headers and no credentials: a simple CORS request, so no preflight.
const healthUrl = () => `${process.env.REACT_APP_BACKEND_URL || ''}/health?r=${nonce()}`;

// Served by our own CDN, not the API.
const siteUrl = () => `${process.env.PUBLIC_URL || ''}/manifest.json?r=${nonce()}`;

// `ms` is the time taken, or null when the request failed or ran out of time; `timedOut` tells which.
// AbortSignal.timeout() is missing before Safari 16, so the timeout is done by hand.
const timedFetch = async (url) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMING.CHECK_TIMEOUT_MS);
    const startedAt = Date.now();

    try {
        // Any HTTP response, a 5xx included, means the network carried the request.
        await fetch(url, { cache: 'no-store', credentials: 'omit', signal: controller.signal });
        return { ms: Date.now() - startedAt, timedOut: false };
    } catch {
        return { ms: null, timedOut: controller.signal.aborted };
    } finally {
        clearTimeout(timer);
    }
};

/**
 * One connection check.
 *
 * @returns {Promise<{ reachable: boolean, ms: number | null, timedOut: boolean }>}
 *   `reachable` — the user is online. `ms` — how long `/health` took, or null when it did not answer.
 *   `timedOut` — unreachable only because a request ran out of time, rather than failing outright.
 */
export const probe = async () => {
    const health = await timedFetch(healthUrl());
    if (health.ms != null) return { reachable: true, ms: health.ms, timedOut: false };

    // /health did not answer. If our own site loads, the user is online and the API is the
    // problem, so this is not reported as offline.
    const site = await timedFetch(siteUrl());
    return {
        reachable: site.ms != null,
        ms: null,
        timedOut: site.ms == null && (health.timedOut || site.timedOut),
    };
};
